import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { ProfileExtrusionType } from "building-types/profiles/profileTypesEnum";
import type {
  BuildingGenerationIssue,
  MissingProfileReferenceIssue,
} from "building-types/settings/buildingGenerationIssue";
import { BuildingGenerationIssueType } from "building-types/settings/buildingGenerationIssue";
import type { ActiveProfileSource } from "building-types/settings/profileCatalogSettings";
import { docEntityHandleHash } from "doc/entity/core/handleHash";
import { getActiveProfileDescriptorOrUndefined } from "./profileTools";

/**
 * THIS IS A TEMP SCAFFOLDING
 * TODO(profile migration): once PROFILE_EXTRUSION_DESCRIPTOR is fully retired in favor of
 * first-class doc.profiles, this can be removed.
 * */
export function isNoneProfileId(profileId: string | undefined | null): boolean {
  return profileId === ProfileExtrusionType.NONE || profileId === undefined || profileId === null || profileId === "";
}

function isRealProfileId(profileId: string | undefined | null): profileId is string {
  return !isNoneProfileId(profileId);
}

export function isMissingCatalogProfile(
  profileSource: ActiveProfileSource,
  profileId: string | undefined | null,
): boolean {
  if (!profileSource.settingsToCatalogs || !isRealProfileId(profileId)) {
    return false;
  }
  return getActiveProfileDescriptorOrUndefined(profileSource, profileId) === undefined;
}

export function pushMissingProfileReferenceIssue(
  issues: BuildingGenerationIssue[] | undefined,
  profileSource: ActiveProfileSource,
  profileId: string | undefined | null,
  handle: DocEntityHandle | undefined,
  field?: string,
): boolean {
  if (!isMissingCatalogProfile(profileSource, profileId)) {
    return false;
  }

  if (issues && handle && profileId) {
    const reportedKeys = getReportedMissingProfileKeys(issues);
    const reportedKey = missingProfileReferenceIssueKey({
      type: BuildingGenerationIssueType.MISSING_PROFILE_REFERENCE,
      profileId,
      handle,
      field,
    });
    if (!reportedKeys.has(reportedKey)) {
      issues.push({
        type: BuildingGenerationIssueType.MISSING_PROFILE_REFERENCE,
        profileId,
        handle,
        field,
      });
      reportedKeys.add(reportedKey);
    }
  }

  return true;
}

function missingProfileReferenceIssueKey(issue: MissingProfileReferenceIssue): string {
  return `${docEntityHandleHash(issue.handle)}|${issue.profileId}|${issue.field ?? ""}`;
}

/**
 * Building generation can push one missing-profile issue per referencing trim/board, so a doc with
 * many trims pointing at a single deleted profile can accumulate a large `issues` array. A per-push
 * linear scan to de-dupe would make that O(n^2); instead cache the set of already-reported keys on
 * the array itself, so repeat calls against the same `issues` reference stay O(1) per push.
 */
const reportedMissingProfileKeysByIssuesArray = new WeakMap<BuildingGenerationIssue[], Set<string>>();

function getReportedMissingProfileKeys(issues: BuildingGenerationIssue[]): Set<string> {
  let keys = reportedMissingProfileKeysByIssuesArray.get(issues);
  if (!keys) {
    keys = new Set(
      issues
        .filter(
          (issue): issue is MissingProfileReferenceIssue =>
            issue.type === BuildingGenerationIssueType.MISSING_PROFILE_REFERENCE,
        )
        .map(missingProfileReferenceIssueKey),
    );
    reportedMissingProfileKeysByIssuesArray.set(issues, keys);
  }
  return keys;
}
